// src/api/movimientosService.js
import { getDataList, getData, postData, updateData, putData, patchData } from './common';

/**
 * Servicio para gestión de movimientos de patrones
 */

/**
 * Crear salida de múltiples patrones (bulk)
 * @param {Object} payload - { patronIds, otIds, responsibleUserId, notes }
 * @returns {Promise} Movimientos creados
 */
export const createMovimientoBulk = async (payload) => {
  try {
    const response = await postData('patronesMovimientos/bulk', payload);
    return response.data;
  } catch (error) {
    console.error('Error al crear movimiento bulk:', error);
    throw error;
  }
};

/**
 * Agregar OT a un movimiento existente
 * @param {String} movementId - ID del movimiento
 * @param {Object} data - { otId, notes }
 * @returns {Promise} Movimiento actualizado
 */
export const agregarOtMovimiento = async (movementId, data) => {
  try {
    const response = await postData(`patronesMovimientos/${movementId}/ots`, data);
    return response.data;
  } catch (error) {
    console.error('Error al agregar OT al movimiento:', error);
    throw error;
  }
};

/**
 * Completar una OT dentro de un movimiento
 * @param {String} movementId - ID del movimiento
 * @param {String} otId - ID de la OT
 * @param {Object} data - { notes }
 * @returns {Promise} Movimiento actualizado
 */
export const completarOtMovimiento = async (movementId, otId, data = {}) => {
  try {
    const response = await patchData(
      `patronesMovimientos/${movementId}/ots/${otId}/complete`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Error al completar OT:', error);
    throw error;
  }
};

/**
 * Cerrar un movimiento (devolución del patrón)
 * @param {String} movementId - ID del movimiento
 * @param {Object} data - { notes, forceClose }
 * @returns {Promise} Movimiento cerrado
 */
export const cerrarMovimiento = async (movementId, data = {}) => {
  try {
    const response = await patchData(
      `patronesMovimientos/${movementId}/close`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Error al cerrar movimiento:', error);
    throw error;
  }
};

/**
 * Obtener movimientos de un grupo (mismo consecutivo)
 * @param {String} groupConsecutive - Consecutivo del grupo (ej: MOV-2026-001)
 * @returns {Promise} Movimientos del grupo
 */
export const getMovimientosPorGrupo = async (groupConsecutive) => {
  try {
    const response = await getDataList('patronesMovimientos',  { groupConsecutive },
      { sort: { startedAt: -1 } }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener movimientos por grupo:', error);
    throw error;
  }
};

/**
 * Obtener movimientos activos de un usuario
 * @param {String} userId - ID del usuario
 * @returns {Promise} Movimientos activos
 */
export const getMovimientosActivosUsuario = async (userId) => {
  try {
    const response = await getDataList('patronesMovimientos', {
      conditions: {
        responsibleUserId: userId,
        status: 'open'
      },
      sort: { startedAt: -1 }
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener movimientos activos del usuario:', error);
    throw error;
  }
};

/**
 * Obtener todos los movimientos activos
 * @returns {Promise} Todos los movimientos abiertos
 */
export const getMovimientosActivos = async () => {
  try {
    const response = await getDataList('patronesMovimientos', 
      { status: 'open' },
      { sort: { startedAt: -1 } }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener movimientos activos:', error);
    throw error;
  }
};



/**
 * Obtener movimientos cerrados (historial general)
 * @param {Object} filters - Filtros { startDate, endDate, userId, limit }
 * @returns {Promise} Movimientos cerrados
 */
export const getMovimientosCerrados = async (filters = {}) => {
  try {
    const conditions = { status: 'closed' };

    if (filters.userId) {
      conditions.responsibleUserId = filters.userId;
    }

    if (filters.startDate || filters.endDate) {
      conditions.startedAt = {};
      if (filters.startDate) {
        conditions.startedAt.$gte = filters.startDate;
      }
      if (filters.endDate) {
        conditions.startedAt.$lte = filters.endDate;
      }
    }

    const response = await getDataList('patronesMovimientos', {
      conditions,
      sort: { closedAt: -1 },
      limit: filters.limit || 100
    });

    return response.data;
  } catch (error) {
    console.error('Error al obtener movimientos cerrados:', error);
    throw error;
  }
};

/**
 * Obtener estadísticas de movimientos
 * @returns {Promise} Estadísticas
 */
export const getMovimientosStats = async () => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [activos, cerradosHoy] = await Promise.all([
      getDataList('patronesMovimientos', 
        { status: 'open' }
      ),
      getDataList('patronesMovimientos', {
          status: 'closed',
          closedAt: { $gte: today.toISOString() }
      })
    ]);

    return {
      ok: true,
      data: {
        movimientosActivos: activos.data.total || 0,
        movimientosCerradosHoy: cerradosHoy.data.total || 0
      }
    };
  } catch (error) {
    console.error('Error al obtener estadísticas de movimientos:', error);
    throw error;
  }
};

/**
 * Obtener un movimiento específico por ID
 * @param {String} movementId - ID del movimiento
 * @returns {Promise} Movimiento
 */
export const getMovimiento = async (movementId) => {
  try {
    const response = await getData('patronesMovimientos', movementId);
    return response.data;
  } catch (error) {
    console.error('Error al obtener movimiento:', error);
    throw error;
  }
};

/**
 * Obtener grupos de movimientos únicos (por consecutivo)
 * @param {Object} filters - Filtros { status, startDate, endDate }
 * @returns {Promise} Grupos únicos
 */
export const getGruposMovimientos = async (filters = {}) => {
  try {
    const conditions = {};

    if (filters.status) {
      conditions.status = filters.status;
    }

    if (filters.startDate || filters.endDate) {
      conditions.startedAt = {};
      if (filters.startDate) {
        conditions.startedAt.$gte = filters.startDate;
      }
      if (filters.endDate) {
        conditions.startedAt.$lte = filters.endDate;
      }
    }

    const response = await getDataList('patronesMovimientos', {
      conditions,
      sort: { startedAt: -1 }
    });

    // Agrupar por groupConsecutive
    const movimientos = response.data.data || [];
    const grupos = {};

    movimientos.forEach(mov => {
      const key = mov.groupConsecutive;
      if (!grupos[key]) {
        grupos[key] = {
          groupConsecutive: key,
          movimientos: [],
          startedAt: mov.startedAt,
          status: mov.status,
          responsibleUserId: mov.responsibleUserId,
          totalPatrones: 0
        };
      }
      grupos[key].movimientos.push(mov);
      grupos[key].totalPatrones++;
    });

    return {
      ok: true,
      data: Object.values(grupos),
      total: Object.keys(grupos).length
    };
  } catch (error) {
    console.error('Error al obtener grupos de movimientos:', error);
    throw error;
  }
};

/**
 * Validar si un patrón está disponible para salida
 * @param {String} patronId - ID del patrón
 * @returns {Promise} { available: boolean, reason: string }
 */
export const validarDisponibilidadPatron = async (patronId) => {
  try {
    const patronResponse = await getData('Patrones', patronId);
    const patron = patronResponse.data.data;

    if (patron.inService) {
      return {
        available: false,
        reason: 'El patrón está actualmente en servicio'
      };
    }

    if (patron.estado !== 'Activo') {
      return {
        available: false,
        reason: `El patrón está en estado: ${patron.estado}`
      };
    }

    return {
      available: true,
      reason: 'Patrón disponible'
    };
  } catch (error) {
    console.error('Error al validar disponibilidad:', error);
    return {
      available: false,
      reason: 'Error al validar disponibilidad'
    };
  }
};
